// src/utils/sellerDoorCodeStore.js
// Firestore side of the way an open house gets into the house: the Seller Provided Door Code (spec
// docs/superpowers/specs/2026-09-18-seller-door-code-design.md, "Data") and the listing's lockboxes (spec
// docs/superpowers/specs/2026-09-25-two-lockboxes-design.md, behaviour 6). Each open house carries its own
// copy of both, snapshotted when it is created, because the emails that go out later read the open house,
// not the deal. So when either changes on the listing page, the listing's UPCOMING open houses get the new
// value here: a Resend Invite or the occupied-home release then carries it. Emails already sent are not
// re-sent.
//
// Writes openHouses only, never a deal, so the sign-in copy of the deals list is unaffected.
// Never import ./firebasefunctions here: keep this file small enough for the listing page to load cheaply.

import { collection, doc, getDoc, getDocs, query, serverTimestamp, where } from "firebase/firestore";
import { firestored } from "../firebase/firebaseConfig";
import { doorCodeOf } from "./sellerDoorCode";
import { LOCKBOX_TYPES } from "./lockboxes";
import { writeBatch } from "./lookOnlyFirestore";
import { assertCanWrite } from "./lookOnly";

const YMD = /^\d{4}-\d{2}-\d{2}$/;

// Open houses store dealId as the deal doc id STRING; a few older docs kept the raw number (same note as
// functions-api/portalTimelineRoutes.js portalOpenHouseKeys). Firestore's "in" filter takes both, so one
// query finds either shape. Only a plain integer string gets the numeric twin.
function openHouseDealKeys(dealId) {
  const id = String(dealId == null ? "" : dealId).trim();
  if (!id) return [];
  const n = Number(id);
  return Number.isFinite(n) && String(n) === id ? [id, n] : [id];
}

// "Cancelled" is the status the app writes (cancelOpenHouse); "Canceled" is the MLS spelling and costs
// nothing to cover.
const isCancelled = (openHouse) => /^cancel+ed$/i.test(String(openHouse?.status || "").trim());

// dealId:   the listing's id (String(deal.createdat), the deal doc id).
// code:     the listing's door code as just saved ("" when it was cleared).
// todayYmd: the viewer's local day, "YYYY-MM-DD". An open house on that day or later counts as upcoming.
// Resolves to the number of open houses updated.
export async function refreshUpcomingOpenHouseDoorCodes(dealId, code, todayYmd) {
  assertCanWrite();
  const keys = openHouseDealKeys(dealId);
  if (!keys.length) return 0;
  // A bad "today" would compare as text against every date and could rewrite past open houses.
  if (!YMD.test(String(todayYmd || ""))) {
    throw new Error("refreshUpcomingOpenHouseDoorCodes: todayYmd must be YYYY-MM-DD");
  }
  const next = doorCodeOf({ sellerDoorCode: code });

  const snap = await getDocs(query(collection(firestored, "openHouses"), where("dealId", "in", keys)));
  const batch = writeBatch(firestored);
  let updated = 0;
  snap.docs.forEach((d) => {
    const openHouse = d.data() || {};
    const date = String(openHouse.date || "");
    if (!YMD.test(date) || date < todayYmd) return;
    if (isCancelled(openHouse)) return;
    if (doorCodeOf(openHouse) === next) return;
    batch.update(doc(firestored, "openHouses", d.id), { sellerDoorCode: next, updatedAt: serverTimestamp() });
    updated += 1;
  });
  if (updated > 0) await batch.commit();
  return updated;
}

// ===== The listing's lockboxes (Henry, 2026-09-25: one Push Button AND one Electronic) =====

const str = (v) => (typeof v === "string" ? v.trim() : typeof v === "number" ? String(v) : "");

const collectionFor = (type) => (type === "Push Button" ? "pushbutton_lockboxes" : "electronic_lockboxes");

// The listing's boxes ([{ type, id, where }], utils/lockboxes.js dealLockboxes) as an open house carries
// them: [{ type, number, code, where }]. Each box is looked up in its own inventory collection, so the open
// house holds the details the emails read and never has to follow the link again. A Push Button's way in is
// its `code`, an Electronic's is its `pin` (snapshotted, never emailed: accessLines drops it). A box that is
// no longer in the inventory keeps its kind with nothing to show, rather than disappearing.
export async function resolveLockboxSnapshots(lockboxes) {
  const out = [];
  for (const raw of Array.isArray(lockboxes) ? lockboxes : []) {
    const type = str(raw && raw.type);
    const id = str(raw && raw.id);
    if (!LOCKBOX_TYPES.includes(type) || !id) continue;
    const box = { type, number: "", code: "", where: str(raw.where) };
    try {
      // eslint-disable-next-line no-await-in-loop -- at most one box of each kind, so at most two reads
      const snap = await getDoc(doc(firestored, collectionFor(type), id));
      const data = snap.exists() ? snap.data() || {} : {};
      box.number = str(data.lockboxNumber);
      box.code = str(type === "Push Button" ? data.code : data.pin);
    } catch (err) {
      console.error("resolveLockboxSnapshots: could not read the lockbox:", id, err);
    }
    out.push(box);
  }
  return out;
}

// Does this open house already say exactly this? Compared field by field, because the snapshots have no id
// and an open house booked before 09/25/2026 has the three old fields and no list at all.
function carriesSnapshots(openHouse, boxes, first) {
  const have = Array.isArray(openHouse.lockboxes) ? openHouse.lockboxes : [];
  if (have.length !== boxes.length) return false;
  const same = boxes.every((b, i) => {
    const h = have[i] || {};
    return str(h.type) === b.type && str(h.number) === b.number && str(h.code) === b.code && str(h.where) === b.where;
  });
  return (
    same &&
    str(openHouse.lockboxType) === first.type &&
    str(openHouse.lockboxNumber) === first.number &&
    str(openHouse.lockboxCode) === first.code
  );
}

// dealId:    the listing's id (String(deal.createdat), the deal doc id).
// lockboxes: the listing's boxes as just saved ([{ type, id, where }]; [] when the last one came off).
// todayYmd:  the viewer's local day, "YYYY-MM-DD". An open house on that day or later counts as upcoming.
// Resolves to the number of open houses updated. The old lockboxType / lockboxNumber / lockboxCode trio is
// written beside the list, in step with the FIRST box, for anything that has not learned the list.
export async function refreshUpcomingOpenHouseLockboxes(dealId, lockboxes, todayYmd) {
  assertCanWrite();
  const keys = openHouseDealKeys(dealId);
  if (!keys.length) return 0;
  // A bad "today" would compare as text against every date and could rewrite past open houses.
  if (!YMD.test(String(todayYmd || ""))) {
    throw new Error("refreshUpcomingOpenHouseLockboxes: todayYmd must be YYYY-MM-DD");
  }
  const boxes = await resolveLockboxSnapshots(lockboxes);
  const first = boxes[0] || { type: "", number: "", code: "" };

  const snap = await getDocs(query(collection(firestored, "openHouses"), where("dealId", "in", keys)));
  const batch = writeBatch(firestored);
  let updated = 0;
  snap.docs.forEach((d) => {
    const openHouse = d.data() || {};
    const date = String(openHouse.date || "");
    if (!YMD.test(date) || date < todayYmd) return;
    if (isCancelled(openHouse)) return;
    if (carriesSnapshots(openHouse, boxes, first)) return;
    batch.update(doc(firestored, "openHouses", d.id), {
      lockboxes: boxes,
      lockboxType: first.type,
      lockboxNumber: first.number,
      lockboxCode: first.code,
      updatedAt: serverTimestamp(),
    });
    updated += 1;
  });
  if (updated > 0) await batch.commit();
  return updated;
}
